import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Clock3,
  Building2,
  ArrowUpRight,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg =
  "https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=1800&auto=format&fit=crop";

const ContactSPV = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 0.9,
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 60,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="Contact SPV"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/75" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/85 via-[#082567]/55 to-[#026B07]/25" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/10 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Contact Information
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95]"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Contact SPV
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            Reach out to the SPV unit for enquiries, partnership opportunities,
            project coordination and operational support.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11]"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link to="/spv" className="hover:text-[#F2AF11]">
              SPV
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">Contact SPV</span>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.85fr_1.15fr] gap-16">
          {/* LEFT */}
          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              SPV Office
            </p>

            <h2
              className="text-5xl md:text-6xl font-black text-[#082567] leading-tight mb-8"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Let’s Discuss Strategic Collaboration
            </h2>

            <p className="text-slate-600 leading-[2] text-[16.5px] mb-10">
              Contact the SPV office for partnership enquiries, operational
              support, project coordination and investment-related discussions.
            </p>

            <div className="space-y-5">
              <div className="flex gap-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-[26px] p-6">
                <div className="w-14 h-14 rounded-2xl bg-[#082567] text-white flex items-center justify-center flex-shrink-0">
                  <Phone size={24} />
                </div>

                <div>
                  <p className="text-[#082567] font-black mb-1">Phone</p>
                  <p className="text-slate-600">+234 XXX XXX XXXX</p>
                </div>
              </div>

              <div className="flex gap-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-[26px] p-6">
                <div className="w-14 h-14 rounded-2xl bg-[#082567] text-white flex items-center justify-center flex-shrink-0">
                  <Mail size={24} />
                </div>

                <div>
                  <p className="text-[#082567] font-black mb-1">Email</p>
                  <p className="text-slate-600">spv@ogfza.gov.ng</p>
                </div>
              </div>

              <div className="flex gap-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-[26px] p-6">
                <div className="w-14 h-14 rounded-2xl bg-[#082567] text-white flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} />
                </div>

                <div>
                  <p className="text-[#082567] font-black mb-1">Address</p>
                  <p className="text-slate-600">
                    Oil and Gas Free Zones Authority,
                    <br />
                    Nigeria.
                  </p>
                </div>
              </div>

              <div className="flex gap-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-[26px] p-6">
                <div className="w-14 h-14 rounded-2xl bg-[#082567] text-white flex items-center justify-center flex-shrink-0">
                  <Clock3 size={24} />
                </div>

                <div>
                  <p className="text-[#082567] font-black mb-1">
                    Working Hours
                  </p>
                  <p className="text-slate-600">
                    Monday - Friday | 8:00AM - 5:00PM
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="reveal-up">
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-[42px] p-8 md:p-10">
              <div className="w-18 h-18 rounded-[24px] bg-[#082567]/5 text-[#082567] flex items-center justify-center mb-8">
                <Building2 size={36} />
              </div>

              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Partnership Support
              </p>

              <h3
                className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-8"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Connect With The SPV Team
              </h3>

              <p className="text-slate-600 leading-[2] text-[16.5px] mb-10">
                The SPV office supports investors, operators and stakeholders by
                facilitating communication, operational coordination and project
                engagement within OGFZA-regulated Free Zones.
              </p>

              <div className="space-y-5">
                <input
                  type="text"
                  placeholder="Full Name"
                  className="w-full h-16 rounded-2xl border border-[#E2E8F0] bg-white px-6 outline-none focus:border-[#082567]"
                />

                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full h-16 rounded-2xl border border-[#E2E8F0] bg-white px-6 outline-none focus:border-[#082567]"
                />

                <textarea
                  rows="6"
                  placeholder="Your Message"
                  className="w-full rounded-2xl border border-[#E2E8F0] bg-white p-6 outline-none focus:border-[#082567] resize-none"
                />

                <button className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#082567] hover:bg-[#026B07] text-white font-black transition-all duration-500">
                  Send Message
                  <ArrowUpRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactSPV;